import React, { useState } from 'react'
import { View, TouchableOpacity, ScrollView, ActivityIndicator, TextInput, StyleSheet } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Text, Icon, FeatureTooltip } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { useFtConversationAiGenerate, useFtConversationAiSummarize, useFtConversationAiAnalyze } from '@hooks'
import { isAiDailyLimitError, translateApiError } from '@utils'

type AiTab = 'generate' | 'summarize' | 'analyze'

interface ConversationAiPanelProps {
  conversationId: string
  onSelectSuggestion: (text: string) => void
  onClose: () => void
  accentColor: string
}

export const ConversationAiPanel: React.FC<ConversationAiPanelProps> = ({
  conversationId,
  onSelectSuggestion,
  onClose,
  accentColor,
}) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)

  const [activeTab, setActiveTab] = useState<AiTab>('generate')
  const [instruction, setInstruction] = useState('')

  const generateMutation = useFtConversationAiGenerate()
  const summarizeMutation = useFtConversationAiSummarize()
  const analyzeMutation = useFtConversationAiAnalyze()

  // Past the daily AI allowance « Réessayez » would be refused again: say the
  // limit instead. Any other failure keeps the retry sentence.
  const dailyLimitError = [generateMutation.error, summarizeMutation.error, analyzeMutation.error].find(
    isAiDailyLimitError,
  )

  const handleGenerate = () => {
    generateMutation.mutate({
      conversationId,
      instruction: instruction.trim() || undefined,
    })
  }

  const handleSummarize = () => {
    summarizeMutation.mutate({ conversationId })
  }

  const handleAnalyze = () => {
    analyzeMutation.mutate({ conversationId })
  }

  const tabs: { key: AiTab; label: string; onPress: () => void }[] = [
    { key: 'generate', label: t('ai.tabSuggest'), onPress: handleGenerate },
    { key: 'summarize', label: t('ai.tabSummarize'), onPress: handleSummarize },
    { key: 'analyze', label: t('ai.tabAnalyze'), onPress: handleAnalyze },
  ]

  const isLoading = generateMutation.isPending || summarizeMutation.isPending || analyzeMutation.isPending

  const handleTabPress = (tab: typeof tabs[0]) => {
    setActiveTab(tab.key)
    if (tab.key !== 'generate') {
      tab.onPress()
    }
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Icon name="star" size={18} color={accentColor} />
          <Text type="bodyBold" style={[styles.headerTitle, { color: accentColor }]}>
            {t('ai.assistant')}
          </Text>
        </View>
        <TouchableOpacity onPress={onClose} style={styles.closeButton}>
          <Icon name="cross" size={16} color={theme.colors.greyBlue} />
        </TouchableOpacity>
      </View>

      <View style={styles.tabsContainer}>
        {tabs.map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[
              styles.tab,
              activeTab === tab.key && { backgroundColor: accentColor },
            ]}
            onPress={() => handleTabPress(tab)}
          >
            <Text
              type="bodySmall"
              style={[
                styles.tabText,
                activeTab === tab.key && styles.activeTabText,
              ]}
            >
              {tab.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      <FeatureTooltip
        id="conversation_ai_panel"
        description={t('featureTooltip.conversation_ai_panel.description')}
        icon="star"
        compact
      />

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {activeTab === 'generate' && (
          <View>
            <TextInput
              style={styles.instructionInput}
              value={instruction}
              onChangeText={setInstruction}
              placeholder={t('ai.instructionsPlaceholder')}
              placeholderTextColor={theme.colors.greyBlue}
              multiline
            />
            <TouchableOpacity
              style={[styles.actionButton, { backgroundColor: accentColor }]}
              onPress={handleGenerate}
              disabled={generateMutation.isPending}
            >
              {generateMutation.isPending ? (
                <ActivityIndicator color={theme.colors.white} size="small" />
              ) : (
                <Text type="bodySmall" style={styles.actionButtonText}>
                  {t('ai.generateSuggestions')}
                </Text>
              )}
            </TouchableOpacity>

            {generateMutation.data?.suggestions && (
              <View style={styles.suggestionsContainer}>
                {generateMutation.data.suggestions.map((suggestion, index) => (
                  <TouchableOpacity
                    key={index}
                    style={[styles.suggestionCard, { borderColor: accentColor + '40' }]}
                    onPress={() => onSelectSuggestion(suggestion)}
                  >
                    <Text type="bodySmall" style={styles.suggestionText}>
                      {suggestion}
                    </Text>
                    <View style={[styles.useButton, { backgroundColor: accentColor + '20' }]}>
                      <Text type="bodyExtraSmall" style={{ color: accentColor }}>
                        {t('ai.use')}
                      </Text>
                    </View>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>
        )}

        {activeTab === 'summarize' && (
          <View>
            {summarizeMutation.isPending && (
              <View style={styles.loadingContainer}>
                <ActivityIndicator color={accentColor} size="small" />
                <Text type="bodySmall" style={styles.loadingText}>
                  {t('ai.summaryInProgress')}
                </Text>
              </View>
            )}
            {summarizeMutation.data?.summary && (
              <View style={[styles.resultCard, { borderColor: accentColor + '40' }]}>
                <Text type="bodySmall" style={styles.resultText}>
                  {summarizeMutation.data.summary}
                </Text>
              </View>
            )}
          </View>
        )}

        {activeTab === 'analyze' && (
          <View>
            {analyzeMutation.isPending && (
              <View style={styles.loadingContainer}>
                <ActivityIndicator color={accentColor} size="small" />
                <Text type="bodySmall" style={styles.loadingText}>
                  {t('ai.analysisInProgress')}
                </Text>
              </View>
            )}
            {analyzeMutation.data && (
              <View style={styles.analyzeResults}>
                <View style={[styles.analyzeSection, { borderColor: accentColor + '40' }]}>
                  <Text type="bodyBold" style={[styles.analyzeSectionTitle, { color: accentColor }]}>
                    {t('ai.sentiment')}
                  </Text>
                  <Text type="bodySmall" style={styles.resultText}>
                    {analyzeMutation.data.sentiment ?? t('ai.sentimentUndetermined')}
                  </Text>
                </View>

                <View style={[styles.analyzeSection, { borderColor: accentColor + '40' }]}>
                  <Text type="bodyBold" style={[styles.analyzeSectionTitle, { color: accentColor }]}>
                    {t('ai.topics')}
                  </Text>
                  <View style={styles.topicsContainer}>
                    {analyzeMutation.data.topics.map((topic, index) => (
                      <View key={index} style={[styles.topicBadge, { backgroundColor: accentColor + '15' }]}>
                        <Text type="bodyExtraSmall" style={{ color: accentColor }}>
                          {topic}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>

                <View style={[styles.analyzeSection, { borderColor: accentColor + '40' }]}>
                  <Text type="bodyBold" style={[styles.analyzeSectionTitle, { color: accentColor }]}>
                    {t('ai.dynamics')}
                  </Text>
                  <Text type="bodySmall" style={styles.resultText}>
                    {analyzeMutation.data.dynamics}
                  </Text>
                </View>

                {analyzeMutation.data.keyPoints.length > 0 && (
                  <View style={[styles.analyzeSection, { borderColor: accentColor + '40' }]}>
                    <Text type="bodyBold" style={[styles.analyzeSectionTitle, { color: accentColor }]}>
                      {t('ai.keyPoints')}
                    </Text>
                    {analyzeMutation.data.keyPoints.map((point, index) => (
                      <View key={index} style={styles.keyPointRow}>
                        <Text type="bodySmall" style={styles.keyPointBullet}>•</Text>
                        <Text type="bodySmall" style={[styles.resultText, { flex: 1 }]}>
                          {point}
                        </Text>
                      </View>
                    ))}
                  </View>
                )}
              </View>
            )}
          </View>
        )}

        {(generateMutation.isError || summarizeMutation.isError || analyzeMutation.isError) && (
          <View style={styles.errorContainer}>
            <Text type="bodySmall" style={styles.errorText}>
              {dailyLimitError ? translateApiError(t, dailyLimitError).message : t('ai.errorRetry')}
            </Text>
          </View>
        )}
      </ScrollView>
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      backgroundColor: theme.colors.white,
      borderTopWidth: 1,
      borderTopColor: theme.colors.grey,
      maxHeight: 350,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.grey,
    },
    headerLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.xs,
    },
    headerTitle: {
      fontSize: theme.fontSize.medium,
    },
    closeButton: {
      padding: theme.spacing.xs,
    },
    tabsContainer: {
      flexDirection: 'row',
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.sm,
      gap: theme.spacing.xs,
    },
    tab: {
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xs,
      borderRadius: 20,
      backgroundColor: theme.colors.grey,
    },
    tabText: {
      color: theme.colors.secondary,
    },
    activeTabText: {
      color: theme.colors.white,
    },
    content: {
      paddingHorizontal: theme.spacing.md,
      paddingBottom: theme.spacing.md,
    },
    instructionInput: {
      borderWidth: 1,
      borderColor: theme.colors.grey,
      borderRadius: 12,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.sm,
      fontSize: theme.fontSize.small,
      color: theme.colors.secondary,
      marginBottom: theme.spacing.sm,
      maxHeight: 60,
    },
    actionButton: {
      paddingVertical: theme.spacing.sm,
      borderRadius: 20,
      alignItems: 'center',
      marginBottom: theme.spacing.sm,
    },
    actionButtonText: {
      color: theme.colors.white,
      fontWeight: '600',
    },
    suggestionsContainer: {
      gap: theme.spacing.sm,
    },
    suggestionCard: {
      borderWidth: 1,
      borderRadius: 12,
      padding: theme.spacing.md,
    },
    suggestionText: {
      color: theme.colors.secondary,
      marginBottom: theme.spacing.sm,
    },
    useButton: {
      alignSelf: 'flex-end',
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xs,
      borderRadius: 12,
    },
    loadingContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing.lg,
      gap: theme.spacing.sm,
    },
    loadingText: {
      color: theme.colors.greyBlue,
    },
    resultCard: {
      borderWidth: 1,
      borderRadius: 12,
      padding: theme.spacing.md,
    },
    resultText: {
      color: theme.colors.secondary,
      lineHeight: 20,
    },
    analyzeResults: {
      gap: theme.spacing.sm,
      paddingBottom: theme.spacing.md,
    },
    analyzeSection: {
      borderWidth: 1,
      borderRadius: 12,
      padding: theme.spacing.md,
    },
    analyzeSectionTitle: {
      marginBottom: theme.spacing.xs,
      fontSize: theme.fontSize.small,
    },
    topicsContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: theme.spacing.xs,
    },
    topicBadge: {
      paddingHorizontal: theme.spacing.sm,
      paddingVertical: 4,
      borderRadius: 12,
    },
    keyPointRow: {
      flexDirection: 'row',
      gap: theme.spacing.xs,
      marginBottom: 4,
    },
    keyPointBullet: {
      color: theme.colors.secondary,
    },
    errorContainer: {
      padding: theme.spacing.md,
      alignItems: 'center',
    },
    errorText: {
      color: theme.colors.error,
      textAlign: 'center',
    },
  })
